import {
    organizationGroupErrorMessage,
    useRenameOrganizationGroup,
} from "@/api/organization-groups";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { toast } from "sonner";

interface GroupNameFieldProps {
    groupId: number;
    name: string;
}

export function GroupNameField({ groupId, name }: GroupNameFieldProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [failure, setFailure] = useState<string | null>(null);
    const rename = useRenameOrganizationGroup();
    const submission = useSingleFlight();

    const trimmed = draft.trim();
    const canSave = trimmed.length > 0 && trimmed !== name && !rename.isPending;

    const startEditing = () => {
        setDraft(name);
        setFailure(null);
        setEditing(true);
    };

    const cancel = () => {
        if (submission.isRunning()) return;
        setDraft(name);
        setFailure(null);
        setEditing(false);
    };

    const save = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!canSave) return;
        setFailure(null);
        await submission.run(async () => {
            try {
                await rename.mutateAsync({
                    id: groupId,
                    data: { name: draft },
                });
                setEditing(false);
            } catch (error) {
                const message = organizationGroupErrorMessage(
                    error,
                    "Couldn't rename the group. Please try again."
                );
                setFailure(message);
                toast.error(message);
            }
        });
    };

    if (!editing) {
        return (
            <div className="flex min-w-0 items-center gap-2">
                <h1 className="text-foreground truncate text-2xl font-semibold tracking-tight">
                    {name}
                </h1>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={startEditing}
                >
                    Rename
                </Button>
            </div>
        );
    }

    return (
        <form
            onSubmit={save}
            onKeyDown={(event) => {
                if (event.key === "Escape") cancel();
            }}
            className="flex min-w-0 flex-wrap items-end gap-2"
        >
            <div className="grid min-w-0 gap-2">
                <Label htmlFor="organization-group-rename" className="sr-only">
                    Group name
                </Label>
                <Input
                    id="organization-group-rename"
                    maxLength={120}
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    autoComplete="off"
                    disabled={rename.isPending}
                    className="w-64 max-w-full"
                />
            </div>
            <Button type="submit" size="sm" aria-disabled={!canSave}>
                Save
            </Button>
            <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={cancel}
                disabled={rename.isPending}
            >
                Cancel
            </Button>
            {failure && (
                <p role="alert" className="text-destructive w-full text-sm">
                    {failure}
                </p>
            )}
        </form>
    );
}
